import { TFunction } from "i18next";

import {
  errors,
  NO_VALUE_DISPLAY,
  TRANSLATION_KEY,
  TRANSLATION_PARAMS_KEY,
} from "../../constants";
import { isLocalOrJenkinsMode } from "../../helpers/env.helpers";
import { isLabelItem, isTranslatableItem } from "../../helpers/error.helpers";
import { Log } from "../../helpers/log.helpers";
import {
  IApiError,
  ILabelItem,
  ITranslatableItem,
  normalizeErrors,
  TError,
  TTranslationParams,
} from "../../models";
import { joinApiSerializedValidationErrorMessages } from "./api-validation-error.messages.utils";
import { SimpleResult } from "./simple-result";
import {
  localizePermissionTranslationParams,
  processDynamicTranslationParams,
} from "./simple-result.translate.helpers";

export const simpleResultErrorInfo = <T>(
  simpleResult: SimpleResult<T>,
  translateFunction: TFunction,
): string => {
  if (!simpleResult.error) {
    return NO_VALUE_DISPLAY;
  }

  const { error, debugInfo } = simpleResult;

  const validationJoined = joinApiSerializedValidationErrorMessages(
    (error as IApiError)?.validationErrors,
  );

  if (validationJoined) {
    return validationJoined;
  }

  if (isTranslatableItem(error)) {
    if (error.label) {
      Log.debug("Translatable error:", error);
    }
    if (debugInfo) {
      Log.debug("Debug info:", debugInfo);
    }

    const translationParams: TTranslationParams | undefined =
      error[TRANSLATION_PARAMS_KEY];
    const processedParams =
      translationParams &&
      processDynamicTranslationParams(translationParams, translateFunction);

    return translateFunction(
      error.translationKey,
      localizePermissionTranslationParams(
        error.translationKey,
        processedParams,
        translateFunction,
      ),
    );
  }

  if (isLabelItem(error)) {
    return error.label;
  }

  if (Array.isArray(error)) {
    return error.join(", ");
  }

  if (typeof error === "string") {
    return error;
  }

  if ((error as Error)?.message) {
    return (error as Error)?.message;
  }

  const errMessage = translateFunction("errors.unknownError");
  Log.warn(errMessage, error);
  return errMessage;
};

/**
 * Translated text of a non-blocking notice the API attached to a successful result: the action ran, but part of
 * it was deliberately not applied. Undefined when the result carries nothing to show.
 */
export const simpleResultWarnInfo = <T>(
  simpleResult: SimpleResult<T>,
  translateFunction: TFunction,
): string | undefined => {
  const { warn } = simpleResult;

  if (!isTranslatableItem(warn)) {
    return undefined;
  }

  const translationParams: TTranslationParams | undefined =
    warn[TRANSLATION_PARAMS_KEY];
  const processedParams =
    translationParams &&
    processDynamicTranslationParams(translationParams, translateFunction);

  return translateFunction(
    warn.translationKey,
    localizePermissionTranslationParams(
      warn.translationKey,
      processedParams,
      translateFunction,
    ),
  );
};

const hasApiValidationErrors = (value: unknown): boolean => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const validationErrors =
    "validationErrors" in value && Array.isArray(value.validationErrors)
      ? value.validationErrors
      : undefined;
  const apiErrors =
    "errors" in value && Array.isArray(value.errors) ? value.errors : undefined;

  return (
    Boolean(joinApiSerializedValidationErrorMessages(validationErrors)) ||
    (apiErrors?.length ?? 0) > 0
  );
};

export const hasDisplayableSimpleResultError = (
  simpleResult: SimpleResult<unknown>,
): boolean => {
  const { error } = simpleResult;

  if (!error) {
    return false;
  }

  if (
    isTranslatableItem(error) ||
    isLabelItem(error) ||
    typeof error === "string" ||
    Array.isArray(error)
  ) {
    return true;
  }

  return hasApiValidationErrors(error);
};

export const warnAndToastSimpleResult = <T>(
  simpleResult: SimpleResult<T>,
  translateFunction: TFunction,
): void => {
  if (!simpleResult.success) {
    Log.warnAndToastDetailed(
      simpleResultErrorInfo(simpleResult, translateFunction),
      simpleResult.errorAdditionalDetails,
    );

    if (isLocalOrJenkinsMode()) {
      Log.debug(
        "Warn toast details:",
        simpleResult.error,
        simpleResult.errorAdditionalDetails,
        simpleResult.debugInfo,
      );
    }
  }
};

export const warnAndToastSimpleResultWithFallback = <T>(
  simpleResult: SimpleResult<T>,
  translateFunction: TFunction,
  fallbackTranslationKey: string,
): void => {
  if (simpleResult.success) {
    return;
  }

  if (hasDisplayableSimpleResultError(simpleResult)) {
    warnAndToastSimpleResult(simpleResult, translateFunction);
    return;
  }

  Log.warnAndToast(
    translateFunction(fallbackTranslationKey),
    simpleResult.error,
  );
};

export const errorAndToastSimpleResult = <T>(
  simpleResult: SimpleResult<T>,
  translateFunction: TFunction,
): void => {
  if (!simpleResult.success) {
    Log.errorAndToast(
      simpleResultErrorInfo(simpleResult, translateFunction),
      simpleResult.errorAdditionalDetails,
    );

    if (isLocalOrJenkinsMode()) {
      Log.debug(
        "Error toast details:",
        simpleResult.error,
        simpleResult.errorAdditionalDetails,
        simpleResult.debugInfo,
      );
    }
  }
};

export const infoAndToastSimpleResult = <T>(
  simpleResult: SimpleResult<T>,
  translateFunction: TFunction,
): void => {
  if (!simpleResult.success) {
    Log.infoAndToast(
      simpleResultErrorInfo(simpleResult, translateFunction),
      simpleResult.errorAdditionalDetails,
    );

    if (isLocalOrJenkinsMode()) {
      Log.debug(
        "Info toast details:",
        simpleResult.error,
        simpleResult.errorAdditionalDetails,
        simpleResult.debugInfo,
      );
    }
  }
};

export const createErrorResult = <T>(
  translationKey: string,
  label?: string,
  debugInfo?: unknown,
): SimpleResult<T> => {
  return new SimpleResult<T>({
    error: { label, translationKey },
    debugInfo,
  });
};

export const createErrorResultTranslated = <T>(
  translatedError: string,
  debugInfo?: unknown,
): SimpleResult<T> => {
  return new SimpleResult<T>({
    error: translatedError,
    debugInfo,
  });
};

export const createErrorResultExt = <T>({
  translationKey,
  translationParams,
  label,
  data,
  debugInfo,
}: {
  translationKey?: string;
  translationParams?: TTranslationParams;
  label?: string;
  data?: T;
  debugInfo?: unknown;
}): SimpleResult<T> => {
  return new SimpleResult<T>({
    success: false,
    error: { label, translationKey, translationParams } as
      ITranslatableItem | ILabelItem,
    debugInfo,
    data,
  });
};

export const createCatchErrorResult = <T>(
  error: unknown,
  t: TFunction,
): SimpleResult<T> => {
  const errorsNormalized = normalizeErrors(error as IApiError, t);
  return new SimpleResult<T>({ error: errorsNormalized });
};

/* used to cast error result to context typing to eliminate the need
to use " as SimpleResult<any>" */
export const castErrorResult = <T>(
  result: SimpleResult<unknown>,
): SimpleResult<T> => {
  return result as SimpleResult<T>;
};

export const createErrorResultFromException = <T>(
  error: unknown,
  debugInfo?: unknown,
): SimpleResult<T> => {
  return new SimpleResult<T>({
    error: error as TError,
    success: false,
    debugInfo,
  });
};

export const createSuccessResult = <T>(
  data?: T,
  debugInfo?: unknown,
): SimpleResult<T> => {
  return new SimpleResult<T>({ success: true, data, debugInfo });
};

export const createSuccessResultVoid = (
  debugInfo?: unknown,
): SimpleResult<void> => {
  return new SimpleResult<void>({ success: true, debugInfo });
};

const CANCELLED_SIMPLE_RESULT_DEBUG = "SimpleResult.cancelled";

/** User dismissed a confirm. Not a failure — callers must not toast. */
export const createCancelledResult = (): SimpleResult<void> => {
  return createSuccessResultVoid(CANCELLED_SIMPLE_RESULT_DEBUG);
};

export const isCancelledSimpleResult = (
  result: SimpleResult<unknown>,
): boolean =>
  result.success && result.debugInfo === CANCELLED_SIMPLE_RESULT_DEBUG;

/** Success that forwards a notice the API attached, so the caller can toast it instead of a plain "done". */
export const createSuccessResultVoidWithWarn = (
  warn: TError | undefined,
): SimpleResult<void> => {
  return new SimpleResult<void>({ success: true, warn });
};

export const createErrorResultWithNoErrorDetails = <T>(
  debugInfo?: unknown,
): SimpleResult<T> => {
  return new SimpleResult<T>({ success: false, debugInfo });
};

export const simpleResultErrorEmptyResponse = <T>(
  tFunction: TFunction,
): SimpleResult<T> => {
  return new SimpleResult({
    error: tFunction(errors.common.emptyApiResponse),
  });
};

export const simpleResultErrorTechnicalError = <T>(
  tFunction: TFunction,
): SimpleResult<T> => {
  return new SimpleResult({
    error: tFunction(errors.common.technicalProblemOccurred),
  });
};

export const simpleResultInvalidApiResponse = <T>(
  tFunction: TFunction,
): SimpleResult<T> => {
  return new SimpleResult({
    error: tFunction(errors.common.invalidAPIResponse),
  });
};

export const simpleResultAuthenticationError = <T>(
  label?: string,
  debugInfo?: unknown,
): SimpleResult<T> => {
  return createErrorResult(errors.common.authenticationError, label, debugInfo);
};

export const simpleResultDontHavePermissionsError = <T>(
  label?: string,
  debugInfo?: unknown,
): SimpleResult<T> => {
  return createErrorResult(
    errors.common.dontHavePermissionForAction,
    label,
    debugInfo,
  );
};

/* this should never happen and is used as type never - to track
invalid results handling in code, when success flag is true but no expected data is returned */
export const simpleResultInvalidExecutionResult = <T>(
  tFunction: TFunction,
  debugInfo?: unknown,
): SimpleResult<T> => {
  return new SimpleResult({
    error: tFunction(errors.common.invalidExecutionResult),
    debugInfo,
  });
};

export interface IParseSimpleResultError {
  [TRANSLATION_KEY]: string;
  [TRANSLATION_PARAMS_KEY]?: TTranslationParams;
}

export const parseSimpleResultError = (
  fallbackTranslationKey: string,
  result?: SimpleResult<unknown>,
): IParseSimpleResultError => {
  if (isTranslatableItem(result?.error)) {
    const parsed: IParseSimpleResultError = {
      [TRANSLATION_KEY]: result.error[TRANSLATION_KEY],
      [TRANSLATION_PARAMS_KEY]:
        result.error[TRANSLATION_PARAMS_KEY] ?? undefined,
    };
    return parsed;
  }

  const fallback: IParseSimpleResultError = {
    [TRANSLATION_KEY]: fallbackTranslationKey,
  };
  return fallback;
};

export const createErrorResultFromTranslatableItem = <T>({
  translatableItem,
  debugInfo,
}: {
  translatableItem: ITranslatableItem;
  debugInfo?: unknown;
}): SimpleResult<T> => {
  return new SimpleResult<T>({
    error: translatableItem,
    debugInfo,
  });
};

/**
 * True when the request failed without any HTTP response: connection dropped,
 * offline, or the browser canceled the request because a navigation/reload
 * started. Says nothing about auth state, so it must not trigger a logout.
 */
export const isEmptyResponseErrorResult = <T>(
  simpleResult: SimpleResult<T>,
): boolean => {
  return (
    isTranslatableItem(simpleResult.error) &&
    simpleResult.error.translationKey === errors.common.emptyApiResponse
  );
};
